import type { Result } from '../core/result';
import { ok, err } from '../core/result';

export async function fetchTextWithProgress(
  url: string,
  onBytes: (loaded: number, total: number | null) => void,
  fetchImpl: typeof fetch = fetch
): Promise<Result<string, string>> {
  let response: Response;
  try {
    response = await fetchImpl(url);
  } catch (e) {
    return err(`Network error fetching ${url}: ${e instanceof Error ? e.message : String(e)}`);
  }

  if (!response.ok) {
    return err(`HTTP ${response.status} fetching ${url}`);
  }

  const contentLength = response.headers.get('Content-Length');
  const total = contentLength ? parseInt(contentLength, 10) : null;

  if (!response.body) {
    const text = await response.text();
    onBytes(text.length, total);
    return ok(text);
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  const chunks: string[] = [];
  let loaded = 0;

  try {
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;

      loaded += value.length;
      onBytes(loaded, total);
      chunks.push(decoder.decode(value, { stream: true }));
    }

    chunks.push(decoder.decode());
    return ok(chunks.join(''));
  } catch (e) {
    return err(`Error reading ${url}: ${e instanceof Error ? e.message : String(e)}`);
  }
}
